import { ref } from 'vue';
import ApiService from '../services/api.service';
import { stringifyQueryObject } from '@/helpers/stringifyQueryObject';
import { useRoute, useRouter } from 'vue-router';
import { useCache } from './useCashe';

const preparePayload = (queriesObj) => {
  const payload = {};

  if (queriesObj.search) {
    payload.q = queriesObj.search; // Map 'search' to 'q' if it has value
  }

  if (queriesObj.sort !== null && queriesObj.sort !== undefined) {
    payload.s = queriesObj.sort; // Map 'sort' to 's' if it has value
  }

  if (queriesObj.user_id) {
    payload.u = queriesObj.user_id; // Map 'user_id' to 'u' if it has value
  }
  if (queriesObj.tag_id) {
    payload.tag_id = queriesObj.tag_id;
  }
  if (queriesObj.sprint_id) {
    payload.sprint_id = queriesObj.sprint_id;
  }

  return payload;
};
const normalizeTaskQueries = (queriesObj = {}) => {
  const normalizePrimitive = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object' && 'value' in value) return value.value ?? '';
    return value;
  };

  return {
    search: normalizePrimitive(queriesObj.search),
    tag_id: normalizePrimitive(queriesObj.tag_id),
    sort: normalizePrimitive(queriesObj.sort),
    status: Array.isArray(queriesObj.status) ? queriesObj.status : [],
    user_id: normalizePrimitive(queriesObj.user_id),
    sprint_id: normalizePrimitive(queriesObj.sprint_id)
  };
};

const buildTaskStatusParams = (queriesObj) => {
  const payload = preparePayload(queriesObj);
  const params = new URLSearchParams(payload);

  if (Array.isArray(queriesObj.status) && queriesObj.status.length) {
    queriesObj.status.forEach((s) => params.append('status[]', s));
  }

  return params.toString();
};

const taskStatusCache = new Map();

const taskCacheKey = (projectId, paramsString) => `task-status:${projectId}:${paramsString}`;

const clearTaskStatusCachesExceptProject = (projectId) => {
  if (!projectId) return;
  const keepPrefix = `task-status:${projectId}:`;
  taskStatusCache.forEach((cache, key) => {
    if (key.startsWith(keepPrefix)) return;
    cache?.clear?.();
    taskStatusCache.delete(key);
  });

  if (typeof window === 'undefined') return;
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith('cache:task-status:') && !key.startsWith(`cache:${keepPrefix}`))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch {}
};

const clearOtherTaskStatusCaches = (keepKey) => {
  taskStatusCache.forEach((cache, key) => {
    if (key === keepKey) return;
    cache?.clear?.();
    taskStatusCache.delete(key);
  });

  if (typeof window === 'undefined') return;
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith('cache:task-status:') && key !== `cache:${keepKey}`)
      .forEach((key) => window.localStorage.removeItem(key));
  } catch {}
};

const getTaskStatusCache = (projectId, paramsString) => {
  const cacheKey = taskCacheKey(projectId, paramsString);
  // Always keep only the current project/query cache key to avoid cross-project stale state.
  clearTaskStatusCachesExceptProject(projectId);
  clearOtherTaskStatusCaches(cacheKey);

  if (!taskStatusCache.has(cacheKey)) {
    const cache = useCache(
      cacheKey,
      async () => {
        const path = `/project/${projectId}/task_tatus${paramsString ? `?${paramsString}` : ''}`;
        const response = await ApiService.get(path);
        return response.data.data;
      },
      { refreshOnGet: true }
    );
    taskStatusCache.set(cacheKey, cache);
  }

  return { cache: taskStatusCache.get(cacheKey), cacheKey };
};

const invalidateTaskStatusCache = () => {
  taskStatusCache.forEach((cache) => cache?.clear?.());
  taskStatusCache.clear();
  if (typeof window === 'undefined') return;
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith('cache:task-status:'))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch {}
};

const invalidateTaskStatusCacheForProject = (projectId) => {
  if (!projectId) return;
  taskStatusCache.forEach((cache, key) => {
    if (key.startsWith(`task-status:${projectId}:`)) {
      cache?.clear?.();
      taskStatusCache.delete(key);
    }
  });
  if (typeof window === 'undefined') return;
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith(`cache:task-status:${projectId}:`))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch {}
};

const overrideTaskStatusCacheValue = (projectId, normalizedQueries, value) => {
  if (!projectId) return;
  const paramsString = buildTaskStatusParams(normalizedQueries);
  const { cache } = getTaskStatusCache(projectId, paramsString);
  if (cache && typeof cache.set === 'function') cache.set(value);
};
const queries = ref({
  search: '',
  tag_id: '',
  sort: '',
  status: [],
  user_id: '',
  sprint_id: ''
});
const originalPayload = ref('');
export default function useTaskStatus() {
  const isTaskLisLoading = ref(false);
  const route = useRoute();
  const router = useRouter();
  const deletedStatusId = ref(null);
  const taskStatuses = ref([]);
  const taskStatusesListOpt = ref([]);
  const originalTaskStatuses = ref([]);

  const addFilterQueriesToRoute = async (normalizedQueries) => {
    const nextQueries = { ...queries.value, ...normalizedQueries };
    queries.value = nextQueries;
    router.replace(route.path + stringifyQueryObject(nextQueries));
  };

  const applyTaskStatusData = (data, cacheKey) => {
    originalPayload.value = cacheKey;
    originalTaskStatuses.value = data;
    taskStatusesListOpt.value = data.map((taskStatus) => ({
      value: taskStatus.id,
      label: taskStatus.title
    }));
    taskStatuses.value = data;

    if (queries.value?.status?.length) {
      updateItemsAppearing();
    }
  };

  const getTaskStatus = async (projectId, isNeedLoading = false, options = {}) => {
    if (!projectId) return [];
    const { forceRefresh = false } = options;
    const normalizedQueries = normalizeTaskQueries(queries.value);
    const paramsString = buildTaskStatusParams(normalizedQueries);
    const { cache, cacheKey } = getTaskStatusCache(projectId, paramsString);
    const cacheState = typeof cache.getState === 'function' ? cache.getState() : {};
    const hasCachedData = cacheState.data !== null;
    const hasLocalData = Array.isArray(taskStatuses.value) && taskStatuses.value.length > 0;
    const shouldShowLoader = !(hasCachedData || hasLocalData);

    try {
      if (shouldShowLoader) isTaskLisLoading.value = true;
      const data = forceRefresh ? await cache.refresh() : await cache.get();
      await addFilterQueriesToRoute(normalizedQueries);
      applyTaskStatusData(data, cacheKey);
      return data;
    } catch (error) {
      invalidateTaskStatusCache();
      return [];
    } finally {
      if (shouldShowLoader) isTaskLisLoading.value = false;
    }
  };

  const refreshTaskStatuses = async (projectId, withLoader = false) => {
    return getTaskStatus(projectId, withLoader, { forceRefresh: true });
  };

  const changeStatusOfTask = async (taskID, statusID, projectId, statusIndex, isNeedToBeSorted) => {
    // 1) optimistic UI update
    const normalizedQueries = normalizeTaskQueries(queries.value);
    const current = originalTaskStatuses.value;

    const updated = current.map((s) => {
      if (!s.tasks) return s;
      return {
        ...s,
        tasks: s.tasks.map((t) => (t.id === taskID ? { ...t, task_status_id: statusID } : t))
      };
    });

    // ✅ override cache + UI
    overrideTaskStatusCacheValue(projectId, normalizedQueries, updated);
    applyTaskStatusData(updated, taskCacheKey(projectId, buildTaskStatusParams(normalizedQueries)));

    // 2) call API
    try {
      await ApiService.put('/task/' + taskID + '/status-update', { task_status_id: statusID });
      if (isNeedToBeSorted) await changeSortTasks(projectId, statusIndex);
    } catch (e) {
      // if API fails, refresh from server
      await refreshTaskStatuses(projectId);
    }
  };

  const updateItemsAppearing = () => {
    if (queries?.value?.status?.length === 0) {
      taskStatuses.value = originalTaskStatuses.value;
    } else
      taskStatuses.value = originalTaskStatuses.value.filter((taskStatus) => {
        if (queries?.value?.status?.length && queries?.value?.status?.includes(taskStatus?.id)) {
          return taskStatus;
        }
      });
  };
  const changeOrderStatus = async (id) => {
    try {
      const ids = taskStatuses.value.map((item) => item?.id);
      const response = await ApiService.get('/task_status/' + id + '/sort-update/' + ids);
      // AlertService.successMessage(response.data.message);
    } catch (error) {
      await refreshTaskStatuses(id);
    }
    invalidateTaskStatusCache();
  };
  const changeSortTasks = async (id, statusIndex) => {
    try {
      if (taskStatuses?.value && taskStatuses?.value[statusIndex]?.tasks?.length) {
        const ids = taskStatuses?.value[statusIndex]?.tasks?.map((item) => item?.id);
        let statusId = taskStatuses?.value[statusIndex]?.id;
        const response = await ApiService.get('/task/status/' + statusId + '/sort-update/' + ids);
      }
      // AlertService.successMessage(response.data.message);
    } catch (error) {
      await refreshTaskStatuses(id);
    }
    invalidateTaskStatusCache();
  };

  const storeStatus = async (data, project_id) => {
    try {
      const response = await ApiService.post('/task_status', data);
      if (response.status === 201) {
        await refreshTaskStatuses(project_id);

        // AlertService.successMessage(response.data.message);
      }
    } catch (error) {}
  };
  const addSingleTask = async (data, id) => {
    try {
      let isSuccess = false;
      const response = await ApiService.post('/task', data);
      if (response.status === 201) {
        isSuccess = true;
        await refreshTaskStatuses(id);
        // AlertService.successMessage(response.data.message);
        return isSuccess;
      }
    } catch (error) {}
  };

  const UpdateSingleTask = async (task_id, project_id, data) => {
    try {
      let isSuccess = false;
      const response = await ApiService.post('/task/' + task_id, data);
      if (response.status === 200) {
        isSuccess = true;
        await refreshTaskStatuses(project_id);
        return isSuccess;
        // AlertService.successMessage(response.data.message);
      }
    } catch (error) {}
  };

  const deleteSingleTask = async (task_id, _project_id) => {
    try {
      const response = await ApiService.delete('/task/' + task_id);
      if (response.status === 200) {
        // AlertService.successMessage(response.data.message);
        await refreshTaskStatuses(_project_id);
      }
    } catch (error) {}
  };

  const showSingleTaskDetail = async (id) => {
    try {
      return await ApiService.get('/task/' + id);
    } catch (error) {
      return [];
    }
  };

  const updateStatus = async (id, data, project_id) => {
    try {
      const response = await ApiService.put('/task_status/' + id, data);
      if (response.status === 200) {
        // AlertService.successMessage(response.data.message);
        await refreshTaskStatuses(project_id);
      }
    } catch (error) {}
  };

  const destroyStatus = async (id, project_id) => {
    try {
      const response = await ApiService.delete('/task_status/' + id);
      if (response.status === 200) {
        // AlertService.successMessage(response.data.message);
        deletedStatusId.value = id;
        await refreshTaskStatuses(project_id);
      }
    } catch (error) {}
  };

  const addComment = async (data) => {
    return await ApiService.post('/comments', data);
  };
  const deleteComment = async (id) => {
    return await ApiService.delete('/comments/' + id);
  };
  const editComment = async (id, comment) => {
    return ApiService.put('/comments/' + id, { comment });
  };

  return {
    deleteComment,
    deletedStatusId,
    originalPayload,
    taskStatuses,
    changeStatusOfTask,
    getTaskStatus,
    refreshTaskStatuses,
    changeOrderStatus,
    storeStatus,
    updateStatus,
    taskStatusesListOpt,
    updateItemsAppearing,
    destroyStatus,
    changeSortTasks,
    isTaskLisLoading,
    queries,
    editComment,
    addSingleTask,
    showSingleTaskDetail,
    addComment,
    deleteSingleTask,
    UpdateSingleTask,
    invalidateTaskStatusCache,
    invalidateTaskStatusCacheForProject,
    clearTaskStatusCachesExceptProject
  };
}
